import React, { useState, useEffect, useRef } from "react";
import { ModeIcon } from "../../components/ModeIcon";
import { LOGO_PATHS, LOGO_VIEWBOX } from "../../components/logoPaths";

/* ===== 다이어그램 ===== */

/* 면(Fill) vs 선(Stroke) 비교 다이어그램 */
export function FillVsStrokeDiagram({ t }) {
  const ring = "M45 5 C23 5 5 23 5 45 C5 67 23 85 45 85 C67 85 85 67 85 45 C85 23 67 5 45 5Z M45 20 C31 20 20 31 20 45 C20 59 31 70 45 70 C59 70 70 59 70 45 C70 31 59 20 45 20Z";
  const outerPts = [[45,5],[5,45],[45,85],[85,45]];
  const innerPts = [[45,20],[20,45],[45,70],[70,45]];
  const strokePts = [[45,12],[12,45],[45,78],[78,45]];
  return (
    <div className="guide-diagram">
      <div className="guide-diagram-item guide-diagram-ok">
        <svg width="100" height="100" viewBox="0 0 90 90">
          <path d={ring} fill="#222" stroke="none" fillRule="evenodd" />
          {outerPts.map(([x,y],i) => <rect key={`o${i}`} x={x-3} y={y-3} width="6" height="6" fill="#3b82f6" stroke="#fff" strokeWidth="1" />)}
          {innerPts.map(([x,y],i) => <rect key={`i${i}`} x={x-3} y={y-3} width="6" height="6" fill="#3b82f6" stroke="#fff" strokeWidth="1" />)}
          <path d="M45 5 C23 5 5 23 5 45 C5 67 23 85 45 85 C67 85 85 67 85 45 C85 23 67 5 45 5Z" fill="none" stroke="#3b82f6" strokeWidth="0.8" strokeDasharray="3 2" />
          <path d="M45 20 C31 20 20 31 20 45 C20 59 31 70 45 70 C59 70 70 59 70 45 C70 31 59 20 45 20Z" fill="none" stroke="#3b82f6" strokeWidth="0.8" strokeDasharray="3 2" />
        </svg>
        <div className="guide-diagram-badge ok">O</div>
        <span className="guide-diagram-label">{t("guide.cutter.exFill")}</span>
        <span className="guide-diagram-sub">{t("guide.cutter.exFillSub")}</span>
      </div>
      <div className="guide-diagram-vs">vs</div>
      <div className="guide-diagram-item guide-diagram-bad">
        <svg width="100" height="100" viewBox="0 0 90 90">
          <circle cx="45" cy="45" r="33" fill="none" stroke="#222" strokeWidth="14" />
          {strokePts.map(([x,y],i) => <rect key={`s${i}`} x={x-3} y={y-3} width="6" height="6" fill="#3b82f6" stroke="#fff" strokeWidth="1" />)}
          <circle cx="45" cy="45" r="33" fill="none" stroke="#3b82f6" strokeWidth="0.8" strokeDasharray="3 2" />
        </svg>
        <div className="guide-diagram-badge bad">X</div>
        <span className="guide-diagram-label">{t("guide.cutter.exStroke")}</span>
        <span className="guide-diagram-sub">{t("guide.cutter.exStrokeSub")}</span>
      </div>
    </div>
  );
}

/* 닫힌 경로 vs 끊어진 경로 비교 다이어그램 */
export function ClosedVsBrokenDiagram({ t }) {
  return (
    <div className="guide-diagram">
      <div className="guide-diagram-item guide-diagram-ok">
        <svg width="90" height="90" viewBox="0 0 80 80">
          <path d="M40 10 L65 30 L58 60 L22 60 L15 30 Z" fill="none" stroke="#222" strokeWidth="3" />
        </svg>
        <div className="guide-diagram-badge ok">O</div>
        <span className="guide-diagram-label">{t("guide.cutter.exClosed")}</span>
        <span className="guide-diagram-sub">{t("guide.cutter.exClosedSub")}</span>
      </div>
      <div className="guide-diagram-vs">vs</div>
      <div className="guide-diagram-item guide-diagram-bad">
        <svg width="90" height="90" viewBox="0 0 80 80">
          <path d="M40 10 L65 30 L58 60 L22 60 L15 30" fill="none" stroke="#222" strokeWidth="3" />
          <circle cx="40" cy="10" r="4" fill="#ef4444" />
          <circle cx="15" cy="30" r="4" fill="#ef4444" />
        </svg>
        <div className="guide-diagram-badge bad">X</div>
        <span className="guide-diagram-label">{t("guide.cutter.exBroken")}</span>
        <span className="guide-diagram-sub">{t("guide.cutter.exBrokenSub")}</span>
      </div>
    </div>
  );
}

/* ===== 공용 마커 ===== */
export function Marker({ n, display, top, left, active, onClick }) {
  return (
    <div className={`guide-marker ${active ? "active" : ""}`} style={{ top, left }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}>
      {display ?? n}
    </div>
  );
}

/* 팁 토글 — 클릭하면 부드럽게 펼쳐짐 */
function TipToggle({ tip }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`guide-tip guide-marker-tip guide-tip-toggle ${tip.warn ? "guide-tip-warn" : ""} ${tip.toolToggle ? "guide-tip-tool" : ""}`}
      onClick={() => setOpen(!open)}>
      <div className="guide-tip-summary">
        <span>{tip.toolToggle ? "🔧 도구 상세 보기" : tip.warn ? "⚠ 주의사항 보기" : "💡 TIP 보기"}</span>
        <span className={`guide-tip-arrow ${open ? "open" : ""}`}>▾</span>
      </div>
      <div className={`guide-tip-content ${open ? "visible" : ""}`}>
        {tip.sections ? tip.sections.map((sec, i) => (
          <div key={i} className="guide-tip-section">
            <p>{sec.text}</p>
            {sec.extra}
          </div>
        )) : (
          <>
            <p>{tip.text}</p>
            {tip.extra}
          </>
        )}
      </div>
    </div>
  );
}

export function MarkerDescriptions({ markers, activeMarker, tips, inserts }) {
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeMarker != null && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeMarker]);

  return (
    <div className="guide-marker-list">
      {markers.map((m) => (
        <React.Fragment key={m.n}>
          <div ref={activeMarker === m.n ? activeRef : null}
            className={`guide-marker-item ${activeMarker === m.n ? "active" : ""}`}>
            <span className="guide-marker-num">{m.display ?? m.n}</span>
            <span>{m.label}</span>
          </div>
          {tips && tips.filter((tip) => tip.after === m.n).map((tip, i) => (
            <TipToggle key={`tip-${m.n}-${i}`} tip={tip} />
          ))}
          {inserts && inserts.filter((ins) => ins.after === m.n).map((ins, i) => (
            <React.Fragment key={`ins-${m.n}-${i}`}>{ins.content}</React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

/* 공용 SVG 아이콘 */
const I = ({ children, ...props }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" {...props}>{children}</svg>
);

/* ================================================================
   2026-10-16 리뉴얼 화면 맞춤 목업 (gx-*)
   마커는 가리키는 요소 안에 직접 붙인다 — 목업 폭이 바뀌어도 위치가 어긋나지 않게.
================================================================ */
function makePin(markers, activeMarker, setActiveMarker) {
  return (n) => {
    const m = markers.find((x) => x.n === n);
    if (!m) return null;
    return (
      <Marker n={m.n} display={m.display} top={m.top} left={m.left} active={activeMarker === m.n}
        onClick={() => setActiveMarker(activeMarker === m.n ? null : m.n)} />
    );
  };
}

function Num({ n, soft }) {
  return <span className={`gx-n ${soft ? "soft" : ""}`}>{n}</span>;
}

/* AI 도안 예시 그림 = 쿠키스토리 로고 선화 — 캔버스·썸네일 공용 (2026-10-16 김현비: 임시 물고기 그림 대신 로고)
   LOGO_PATHS 의 "line" 조각이 선(채워진 모양)이라 그대로 채우면 펜 선처럼 보인다. width 는 예전 호출부 호환용(미사용). */
function FishSketch({ size = 120, stroke = "#3B2416" }) {
  return (
    <svg width={size} height={size * (270.36 / 403.97)} viewBox={LOGO_VIEWBOX} aria-hidden="true">
      {LOGO_PATHS.filter((p) => p.kind === "line").map((p, i) => <path key={i} d={p.d} fill={stroke} />)}
    </svg>
  );
}

/* ================================================================
   AI 디자인 페이지
================================================================ */
export function DesignLayout({ activeMarker, setActiveMarker, t }) {
  const markers = [
    { n: 1, top: "-9px", left: "-9px", label: t("guide.design.m1") },
    { n: 2, top: "-9px", left: "-9px", label: t("guide.design.m2") },
    { n: 3, top: "-9px", left: "-9px", label: t("guide.design.m3") },
    { n: 4, top: "-9px", left: "-9px", label: t("guide.design.m4") },
    { n: 5, top: "-9px", left: "-9px", label: t("guide.design.m5") },
    { n: 6, top: "10px", left: "10px", label: t("guide.design.m6") },
    { n: 7, top: "-9px", left: "-9px", label: t("guide.design.m7") },
    { n: 8, top: "-10px", left: "6px", label: t("guide.design.m8") },
  ];
  const pin = makePin(markers, activeMarker, setActiveMarker);

  return (
    <div className="guide-layout">
      <div className="guide-mockup gx gx-app" onClick={() => setActiveMarker(null)}>
        {/* ---- 왼쪽 패널 ---- */}
        <div className="gx-side">
          <div className="gx-scroll">
            <div className="gx-h">{t("nav.design")}</div>
            <div className="gx-sub">{t("designUi.subtitle")}</div>

            <div className="gx-seg gx-pin">
              <span className="on">✨ {t("designUi.modeAi")}</span>
              <span>✏️ {t("designUi.modeHand")}</span>
              {pin(1)}
            </div>

            <div className="gx-sec">
              <div className="gx-sh"><Num n={1} /><b>{t("designUi.describe")}</b></div>
              <div className="gx-ta gx-pin">{t("design.placeholder")}{pin(2)}</div>
              <div className="gx-mini-label">{t("designUi.tryThese")}</div>
              <div className="gx-chips">
                <span>{t("designUi.ex1")}</span><span>{t("designUi.ex2")}</span>
              </div>
              <div className="gx-btn choco">{t("designUi.draw")}</div>
            </div>

            <div className="gx-sec">
              <div className="gx-sh"><Num n={2} /><b>{t("designUi.pick")}</b><small>{t("designUi.pickHint")}</small></div>
              <div className="gx-picks gx-pin">
                <span className="on"><FishSketch size={46} width={7} /></span>
                <span><FishSketch size={46} width={7} stroke="#7C6655" /></span>
                <span><FishSketch size={46} width={7} stroke="#7C6655" /></span>
                {pin(3)}
              </div>
            </div>

            <div className="gx-sec">
              <div className="gx-sh"><Num n={3} /><b>{t("design.savedDesigns")}</b></div>
              <div className="gx-saved gx-pin">
                <span className="gx-saved-th"><FishSketch size={26} width={9} /></span>
                <span className="gx-saved-nm">{t("designUi.ex1")}</span>
                <span className="gx-x">×</span>
                {pin(7)}
              </div>
            </div>
          </div>

          <div className="gx-actions gx-pin">
            <div className="gx-btn caramel">{t("designUi.toCutter")} →</div>
            <div className="gx-mini3">
              <span>{t("design.save")}</span><span>↓ PNG</span><span>↓ SVG</span>
            </div>
            {pin(8)}
          </div>
        </div>

        {/* ---- 오른쪽 그림판 ---- */}
        <div className="gx-main">
          <div className="gx-toolbar">
            <div className="gx-tools gx-pin">
              <span className="gx-tg">
                <span className="gx-tool on"><I stroke="#fff"><path d="M4 3l7 17 2.5-7.5L21 10z" /></I>{t("designUi.toolSelectShort")}</span>
                <span className="gx-tool"><I stroke="#3B2416"><path d="M4 18c4-10 12-10 16 0" /></I>{t("designUi.toolNodeShort")}</span>
              </span>
              <span className="gx-tg">
                <span className="gx-ic"><I stroke="#3B2416"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /></I></span>
                <span className="gx-ic"><I stroke="#3B2416"><line x1="5" y1="19" x2="19" y2="5" /></I></span>
                <span className="gx-ic"><I stroke="#3B2416"><circle cx="12" cy="12" r="8" /></I></span>
                <span className="gx-ic"><I stroke="#3B2416"><rect x="4" y="4" width="16" height="16" rx="1" /></I></span>
                <span className="gx-ic"><I stroke="#3B2416"><path d="M20 20H7L3 16l9-9 8 8-4 4" /></I></span>
              </span>
              <span className="gx-tg">
                <span className="gx-ic"><I stroke="#3B2416"><path d="M3 10h11a6 6 0 0 1 0 12H8" /><path d="M7 6L3 10l4 4" /></I></span>
                <span className="gx-ic"><I stroke="#3B2416"><path d="M21 10H10a6 6 0 0 0 0 12h6" /><path d="M17 6l4 4-4 4" /></I></span>
              </span>
              {pin(4)}
            </div>
            <div className="gx-tools gx-tools-r">
              <span className="gx-tg">
                <span className="gx-ic"><I stroke="#3B2416"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4 M8 11h6" /></I></span>
                <span className="gx-ic"><I stroke="#3B2416"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4 M11 8v6 M8 11h6" /></I></span>
              </span>
            </div>
          </div>
          <div className="gx-subbar gx-pin">
            <b>{t("designUi.thickness")}</b>
            <span className="gx-range"><i style={{ width: "55%" }} /></span>
            <span>+2px</span>
            {pin(5)}
          </div>
          <div className="gx-board">
            <div className="gx-paper gx-pin">
              <FishSketch size={190} width={6} />
              {pin(6)}
            </div>
          </div>
        </div>
      </div>
      <MarkerDescriptions markers={markers} activeMarker={activeMarker}
        tips={[
          { after: 2, tipLabel: t("guide.tipLabel"), text: t("guide.design.tip") },
          { after: 4, toolToggle: true, extra: <ToolGuide t={t} /> },
          { after: 8, warn: true, text: t("guide.cutter.tip3"), extra: <ClosedVsBrokenDiagram t={t} /> },
        ]}
      />
    </div>
  );
}

/* 도구 모음 가이드 — 아이콘 + 설명 항상 표시 */
const TI = ({ children }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

function ToolGuide({ t }) {
  const tools = [
    { icon: <TI><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/></TI>, key: "m3_t1" },
    { icon: <TI><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3" fill="#6366f1"/></TI>, key: "m3_t2" },
    { icon: <TI><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></TI>, key: "m3_t3" },
    { icon: <TI><line x1="5" y1="19" x2="19" y2="5"/></TI>, key: "m3_t4" },
    { icon: <TI><circle cx="12" cy="12" r="9"/></TI>, key: "m3_t5" },
    { icon: <TI><rect x="4" y="4" width="16" height="16" rx="1"/></TI>, key: "m3_t6" },
    { icon: <TI><path d="M20 20H7L3 16l9-9 8 8-4 4"/></TI>, key: "m3_t7" },
    { icon: <TI><rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="3 2"/></TI>, key: "m3_t8" },
    { icon: <><TI><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></TI><TI><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></TI></>, key: "m3_t9" },
    { icon: <><TI><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></TI><TI><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></TI><TI><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></TI></>, key: "m3_t10" },
  ];

  return (
    <div className="guide-tool-guide">
      <div className="guide-tool-grid">
        {tools.map((item, i) => (
          <div key={i} className="guide-tool-item">
            <div className="guide-tool-icon">{item.icon}</div>
            <span className="guide-tool-desc">{t(`guide.design.${item.key}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================================================================
   쿠키커터 만들기
================================================================ */
export function CutterLayout({ activeMarker, setActiveMarker, t }) {
  const markers = [
    { n: 1, top: "-9px", left: "-9px", label: t("guide.cutter.m1") },
    { n: 2, top: "12px", left: "12px", label: t("guide.cutter.m2") },
    { n: 3, top: "-9px", left: "-9px", label: t("guide.cutter.m3") },
    { n: 4, top: "-9px", left: "-9px", label: t("guide.cutter.m4") },
    { n: 5, top: "-9px", left: "-9px", label: t("guide.cutter.m5") },
    { n: 6, top: "-9px", left: "-9px", label: t("guide.cutter.m6") },
    { n: 7, top: "-9px", left: "-9px", label: t("guide.cutter.m7") },
  ];
  const pin = makePin(markers, activeMarker, setActiveMarker);
  const presets = [["small", 50], ["medium", 70], ["large", 90], ["xlarge", 120]];

  return (
    <div className="guide-layout">
      <div className="guide-mockup gx gx-app" onClick={() => setActiveMarker(null)}>
        <div className="gx-side">
          <div className="gx-scroll">
            <div className="gx-h">{t("nav.cutter")}</div>
            <div className="gx-sub">{t("cutterUi.subtitle")}</div>

            <div className="gx-sec">
              <div className="gx-sh"><Num n={1} /><b>{t("cutterUi.secImage")}</b><small>PNG · SVG</small></div>
              <div className="gx-drop gx-pin">
                {t("cutterUi.dropOr")} <b>{t("cutter.selectSvg")}</b>
                {pin(1)}
              </div>
              <div className="gx-mini-label">{t("cutterUi.noImage")} <b className="gx-car">{t("cutterUi.aiDesign")}</b></div>
            </div>

            <div className="gx-sec">
              <div className="gx-sh"><Num n={2} /><b>{t("cutterUi.secMode")}</b></div>
              <div className="gx-modes gx-pin">
                {[["both", "modeBoth"], ["cutter", "modeCutterOnly"], ["stamp", "modeStampOnly"]].map(([k, key]) => (
                  <span key={k} className={k === "both" ? "on" : ""}><ModeIcon kind={k} />{t(`cutterUi.${key}`)}</span>
                ))}
                {pin(3)}
              </div>
            </div>

            <div className="gx-sec">
              <div className="gx-sh"><Num n={3} /><b>{t("cutterUi.secSize")}</b><span className="gx-unit"><i className="on">mm</i><i>in</i></span></div>
              <div>
                <div className="gx-big"><b>70</b> mm <small>{t("cutterUi.longSideHelp")}</small></div>
                <span className="gx-range wide"><i style={{ width: "33%" }} /></span>
                <div className="gx-presets gx-pin">
                  {presets.map(([k, mm]) => (
                    <span key={k} className={k === "medium" ? "on" : ""}>{t(`cutterUi.preset.${k}`)}<small>{mm}mm</small></span>
                  ))}
                  {pin(4)}
                </div>
              </div>
            </div>

            <div className="gx-adv gx-pin">
              <div className="gx-adv-h"><Num n="+" soft /><b>{t("cutterUi.secDetail")}</b><span>{t("cutterUi.expand")}</span></div>
              <div className="gx-adv-sum">{t("cutter.knifeHeight")} 15mm · {t("cutter.knifeThickness")} 1.2mm</div>
              {pin(5)}
            </div>
          </div>

          <div className="gx-actions">
            <div className="gx-btn caramel gx-pin">{t("cutter.downloadStl")} <small>· {t("cutterUi.freeUnlimited")}</small>{pin(6)}</div>
            <div className="gx-btn line gx-pin">{t("cutterUi.orderThis")} →{pin(7)}</div>
          </div>
        </div>

        <div className="gx-main gx-cream">
          <div className="gx-dz gx-pin">
            <div className="gx-dz-ic">↑</div>
            <div className="gx-dz-t">{t("cutterUi.dropTitle")}</div>
            <div className="gx-dz-s">{t("cutterUi.dropSub")}</div>
            <div className="gx-dz-row">
              <span className="gx-btn choco sm">{t("cutter.selectSvg")}</span>
              <span className="gx-btn line sm">{t("cutterUi.noImageAi")}</span>
            </div>
            <div className="gx-dz-ex">
              <span>{t("cutterUi.tryExample")}</span>
              {["example-logo.svg", "example-dog.svg", "example-panda.svg"].map((f) => (
                <i key={f}><img src={`/home/${f}`} alt="" /></i>
              ))}
            </div>
            {pin(2)}
          </div>
        </div>
      </div>
      <MarkerDescriptions markers={markers} activeMarker={activeMarker}
        tips={[
          { after: 1, warn: true, sections: [
            { text: t("guide.cutter.tip2"), extra: <FillVsStrokeDiagram t={t} /> },
            { text: t("guide.cutter.tip3"), extra: <ClosedVsBrokenDiagram t={t} /> },
          ]},
          { after: 5, tipLabel: t("guide.tipLabel"), text: t("guide.cutter.tip") },
        ]}
      />
    </div>
  );
}

/* ================================================================
   갤러리
================================================================ */
const GAL_CARDS = [
  ["fish.png", "Fish-shaped pastry"], ["cat.png", "Cat with Fish"], ["dino.png", "Dinosaur"], ["rocket.png", "Rocket"],
  ["duck.png", "Duck"], ["icecream.png", "Ice Cream"], ["bread.png", "Bread"], ["hamster.png", "Hamster"],
];

export function GalleryLayout({ activeMarker, setActiveMarker, t }) {
  const markers = [
    { n: "g1", display: 1, top: "-9px", left: "-9px", label: t("guide.gallery.m1") },
    { n: "g2", display: 2, top: "-9px", left: "-9px", label: t("guide.gallery.m2") },
    { n: "g3", display: 3, top: "6px", left: "6px", label: t("guide.gallery.m3") },
    { n: "g4", display: 4, top: "-9px", left: "-9px", label: t("guide.gallery.m4") },
    { n: "g5", display: 5, top: "-9px", left: "-9px", label: t("guide.gallery.m5") },
  ];
  const pin = makePin(markers, activeMarker, setActiveMarker);

  return (
    <>
    <div className="guide-layout">
      <div className="guide-mockup gx gx-page" onClick={() => setActiveMarker(null)}>
        <div className="gx-gal-hero">
          <div>
            <span className="gx-pill">{t("galleryUi.heroPill")}</span>
            <div className="gx-ttl">{t("galleryUi.title")}</div>
            <div className="gx-sub">{t("galleryUi.sub")}</div>
          </div>
          <div className="gx-gal-up">
            <span className="gx-btn choco sm gx-pin">{t("galleryUi.uploadBtn")}{pin("g5")}</span>
            <small>{t("galleryUi.uploadNote")}</small>
          </div>
        </div>
        <div className="gx-gal-bar">
          <span className="gx-search gx-pin">
            <I stroke="#7C6655"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></I>
            {t("galleryUi.searchPlaceholder")}
            {pin("g1")}
          </span>
          <span className="gx-sort gx-pin"><i className="on">{t("galleryUi.sortLatest")}</i><i>{t("galleryUi.sortPopular")}</i>{pin("g2")}</span>
        </div>
        <div className="gx-count">{t("galleryUi.countLabel")} <b>{t("galleryUi.countUnit", { count: 23 })}</b></div>
        <div className="gx-gal-grid">
          {GAL_CARDS.map(([img, name], i) => (
            <div key={img} className={`gx-card ${i === 0 ? "gx-pin" : ""}`}>
              <div className="gx-card-th">
                <img src={`/guide/thumbs/${img}`} alt="" />
                <span className={`gx-heart ${i === 1 ? "gx-pin" : ""}`}>♡{i === 1 && pin("g4")}</span>
              </div>
              <div className="gx-card-nm">{name}</div>
              <div className="gx-card-meta"><span>cookistory</span><span>↓ {i % 3} <em>♥</em> {(i + 1) % 3}</span></div>
              {i === 0 && pin("g3")}
            </div>
          ))}
        </div>
      </div>
      <MarkerDescriptions markers={markers} activeMarker={activeMarker} />
    </div>

    {/* ---- 올리기 화면 (별도 섹션) ---- */}
    <div className="guide-write-section">
      <h2 className="guide-section-title">{t("guide.gallery.writeTitle")}</h2>
      <p className="guide-section-desc">{t("guide.gallery.writeDesc")}</p>
      <p className="guide-write-warn">❗ {t("guide.gallery.writeTip")}</p>
      <GalleryWriteLayout activeMarker={activeMarker} setActiveMarker={setActiveMarker} t={t} />
    </div>
    </>
  );
}

/* 갤러리 올리기 화면 목업 */
function GalleryWriteLayout({ activeMarker, setActiveMarker, t }) {
  const markers = [
    { n: "w1", display: 1, top: "-9px", left: "-9px", label: t("guide.gallery.wm1") },
    { n: "w2", display: 2, top: "-9px", left: "-9px", label: t("guide.gallery.wm2") },
    { n: "w3", display: 3, top: "-9px", left: "-9px", label: t("guide.gallery.wm3") },
    { n: "w4", display: 4, top: "-9px", left: "-9px", label: t("guide.gallery.wm4") },
    { n: "w5", display: 5, top: "-9px", left: "-9px", label: t("guide.gallery.wm5") },
  ];
  const pin = makePin(markers, activeMarker, setActiveMarker);

  return (
    <div className="guide-layout">
      <div className="guide-mockup gx gx-page gx-write" onClick={() => setActiveMarker(null)}>
        <div className="gx-ttl">{t("galleryUi.writeTitle")}</div>
        <div className="gx-sub">{t("galleryUi.writeSub")}</div>

        <div className="gx-wsec">
          <div className="gx-sh"><Num n={1} /><b>{t("galleryUi.step1")}</b><small>{t("galleryUi.step1Note")}</small></div>
          <div className="gx-two gx-pin">
            <div className="gx-fbox"><b>{t("galleryUi.svgBox")}</b><em>.svg</em><span>✎</span><small>{t("galleryUi.dropText")} <u>{t("galleryUi.dropPick")}</u></small></div>
            <div className="gx-fbox"><b>{t("galleryUi.stlBox")}</b><em>.stl</em><span>⬢</span><small>{t("galleryUi.dropText")} <u>{t("galleryUi.dropPick")}</u></small></div>
            {pin("w1")}
          </div>
        </div>

        <div className="gx-wsec">
          <div className="gx-sh"><Num n={2} /><b>{t("galleryUi.step2")}</b><small>{t("galleryUi.step2Note")}</small></div>
          <div className="gx-lab">{t("galleryUi.titleLabel")}<span className="gx-auto gx-pin">{t("galleryUi.autoFill")}{pin("w2")}</span></div>
          <div className="gx-input gx-pin">Fish-shaped pastry{pin("w3")}</div>
          <div className="gx-lab">{t("galleryUi.descLabel")}</div>
          <div className="gx-input tall">A cute fish-shaped pastry cookie cutter.</div>
          <div className="gx-lab">{t("galleryUi.tagsLabel")}<em>{t("galleryUi.tagsHint")}</em></div>
          <div className="gx-input gx-tags gx-pin"><i>#fish ×</i><i>#bread ×</i><i>#cute ×</i>{pin("w4")}</div>
        </div>

        <div className="gx-wsec">
          <div className="gx-sh"><Num n={3} /><b>{t("galleryUi.step3")}</b></div>
          <div className="gx-pv">
            <span className="gx-pv-th"><img src="/guide/thumbs/fish.png" alt="" /></span>
            <span><b>Fish-shaped pastry</b><br />{t("galleryUi.thumbNote")}<br />{t("galleryUi.reviewNote")}</span>
          </div>
          <div className="gx-btn choco gx-pin">{t("galleryUi.submit")}{pin("w5")}</div>
        </div>
      </div>
      <MarkerDescriptions markers={markers} activeMarker={activeMarker} />
    </div>
  );
}

/* ================================================================
   내 드라이브 (마이페이지 안) — 2026-10-16 새 마이페이지 디자인
================================================================ */
/* [썸네일, 종류(stl|design), 이름(STL=파일명, 도안=i18n 키), 날짜] */
const DRIVE_CARDS = [
  ["cat.png", "design", "phDesign1", "10.14"],
  ["bat.png", "stl", "bat_cutter.stl", "10.13"],
  ["hamster.png", "design", "phDesign2", "10.12"],
  ["fish.png", "stl", "fish_cutter.stl", "10.11"],
  ["dino.png", "stl", "dino_cutter.stl", "10.9"],
  ["duck.png", "design", "phDesign3", "10.8"],
  ["rocket.png", "stl", "rocket.stl", "10.6"],
  ["icecream.png", "design", "phDesign4", "10.5"],
];

export function DriveLayout({ activeMarker, setActiveMarker, t }) {
  const markers = [
    { n: 1, top: "-2px", left: "-21px", label: t("guide.drive.m1") },
    { n: 2, top: "-11px", left: "-11px", label: t("guide.drive.m2") },
    { n: 3, top: "-2px", left: "-23px", label: t("guide.drive.m3") },
    { n: 4, top: "-4px", left: "calc(100% + 3px)", label: t("guide.drive.m4") },
    { n: 5, top: "calc(50% - 12px)", left: "-14px", label: t("guide.drive.m5") },
  ];
  const pin = makePin(markers, activeMarker, setActiveMarker);
  const stlCount = DRIVE_CARDS.filter((c) => c[1] === "stl").length;
  const designCount = DRIVE_CARDS.length - stlCount;

  return (
    <div className="guide-layout">
      <div className="guide-mockup gx gx-app gx-drive" onClick={() => setActiveMarker(null)}>
        <div className="gx-mside">
          <div className="gx-mprof">
            <span className="gx-mav">C</span>
            <span className="gx-mprof-t"><b>cookistory</b><small>you@example.com</small></span>
          </div>
          <small>{t("mypage.sidebar.groupWork")}</small>
          <span className="on">{t("mypage.sidebar.drive")}</span>
          <span>{t("mypage.sidebar.gallery")}</span>
          <small>{t("mypage.sidebar.groupAccount")}</small>
          <span>{t("mypage.sidebar.account")}</span>
        </div>
        <div className="gx-main gx-dmain">
          <div className="gx-dhead">
            <div>
              <div className="gx-dh">{t("mypage.home.myDrive")}</div>
              <div className="gx-sub">{t("mypage.home.desc")}</div>
            </div>
            <div className="gx-store gx-pin">
              <small>{t("mypage.home.storage")}</small>
              <b>{t("mypage.account.storageDesigns")} {designCount}<i>/10</i> · STL {stlCount}<i>/10</i></b>
              <span className="gx-store-bar"><i style={{ width: `${((designCount + stlCount) / 20) * 100}%` }} /></span>
              <small>{t("mypage.home.retentionRule", { days: 30 })}</small>
              {pin(2)}
            </div>
          </div>
          <div className="gx-dtool">
            <span className="gx-dtabs gx-pin">
              <i className="on">{t("mypage.home.tabAll")} {DRIVE_CARDS.length}</i>
              <i>{t("mypage.home.chipStl")} {stlCount}</i>
              <i>{t("mypage.home.chipDesigns")} {designCount}</i>
              <i>🗑 {t("mypage.home.tabTrash")}</i>
              {pin(1)}
            </span>
            <span className="gx-dtool-r">
              <span className="gx-dpill gx-pin">{t("mypage.home.select")}{pin(3)}</span>
              <span className="gx-dpill">{t("mypage.home.sortNew")} ▾</span>
            </span>
          </div>
          <div className="gx-dsec-t">{t("mypage.home.files")}</div>
          <div className="gx-dgrid">
            {DRIVE_CARDS.map(([img, kind, name, date], i) => (
              <div key={img} className="gx-dcardi">
                <span className="gx-dthumb">
                  <img src={`/guide/thumbs/${img}`} alt="" />
                  <span className={`gx-kind ${kind === "stl" ? "is-stl" : "is-design gx-pin"}`}>
                    {kind === "stl" ? "STL" : t("mypage.home.chipDesigns")}
                    {i === 0 && pin(4)}
                  </span>
                  <span className={`gx-more ${i === 1 ? "on" : ""}`}>⋯</span>
                  {i === 1 && (
                    <span className="gx-dmenu gx-pin">
                      <i>⬇ {t("mypage.home.download")}</i>
                      <i>✎ {t("mypage.home.continueEdit")}</i>
                      <i className="del">🗑 {t("mypage.home.moveToTrash")}</i>
                      {pin(5)}
                    </span>
                  )}
                </span>
                <b>{kind === "stl" ? name : t(`guide.drive.${name}`)}</b>
                <small>{date}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
      <MarkerDescriptions markers={markers} activeMarker={activeMarker}
        tips={[
          { after: 5, tipLabel: t("guide.tipLabel"), text: t("guide.drive.tip") },
        ]}
      />
    </div>
  );
}
